import { isNil } from "lodash";
import moment from "moment";
import React, { Dispatch, ReactNode, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import styled from "styled-components";
import { isNull, isUndefined } from "underscore";
import { IDataTableRow } from "../../../../components/DataTable/DataTableBody/DataTableBody";
import {
  DataTableContextFilters,
  useDataTableContext,
} from "../../../../components/DataTable/DataTableContext/DataTableContextProvider";
import { FilterType, IFilteredColumn } from "../../../../components/DataTable/DataTableFilters/DataTableFilters";
import DelegeeStatus, { Statuses } from "../../../../components/DelegeeStatus/DelegeeStatus";
import { FmpFilters } from "../../../../constants/filtering.constants";
import useAppContext from "../../../../context/hooks/useAppContext";
import useFleetManagerContext from "../../../../context/hooks/useFleetManagerContext";
import useMyProductsContext from "../../../../context/hooks/useMyProductsContext";
import { useOnBulkImportFinishedCallback } from "../../../../hooks/useOnBulkImportFinishedCallback";
import { ForFleetManager } from "../../../../models/ForFleetManager";
import { ContractType, ParkingProductModel, ParkingProductType } from "../../../../models/ParkingProductModel";
import {
  DelegatedParkingProductModel,
  DelegatedParkingRight,
} from "../../../../models/delegations/DelegatedParkingProductsModel";
import { WithFiltersAndPaging } from "../../../../models/filters/WithFiltersAndPaging";
import { useAppDispatch, useAppSelector } from "../../../../store";
import parkingProductsActions from "../../../../store/actions/parkingProducts.actions";
import * as parkingProductsThunks from "../../../../store/actions/parkingProducts.thunks";
import { RootReducer } from "../../../../store/reducers";
import FleetManagerDataTable, {
  IFleetManagerDataTableModel,
} from "../../FleetManagerDataTable/FleetManagerDataTable";
import filteredColumnObjectBuilder from "../ObjectBuilders/FilteredColumnObjectBuilder";
import TruncatedText from "../../../../components/TruncatedText/TruncatedText";
import { convertToRemainingFlex, ProductChip, selectUseDirectVehicleAssignment, useCurrentlySelectedParkingProduct, useGetOccupiedParkingRightsCount, useProductOptions, useSharedProductsMenuAction } from "./common";

const Wrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
`;

export interface IParkingRightsDataTableModel extends IFleetManagerDataTableModel {
  product: JSX.Element;
}

interface FleetMembersTableProps extends StateProps, DispatchProps {
  subHeading?: ReactNode,
  selectedParkingProduct: ParkingProductModel[] | undefined;
  onRevokeClicked: () => void;
}

const getDelegeeStatus = (parkingRight: DelegatedParkingProductModel) => {
  return parkingRight.status as Statuses;
};

const _createData = (delegatedParkingRight: DelegatedParkingRight, showFlex?: boolean): IParkingRightsDataTableModel => {
  let data = {
    rowKey: delegatedParkingRight.parkingRight.parkingRightId,
    name: <TruncatedText text={delegatedParkingRight.delegee.name} />,
    email: <TruncatedText text={delegatedParkingRight.delegee.emailAddress} />,
    product: <Wrapper><ProductChip location={delegatedParkingRight.parkingRight.location} name={delegatedParkingRight.parkingRight.name} /></Wrapper>,
    invitationDate: moment(delegatedParkingRight.parkingRight.created).format("DD/MM/YYYY"),
  } as any

  if (showFlex) { 
    data = {
      ...data,
      remainingFlex: convertToRemainingFlex(delegatedParkingRight.parkingRight.flexBudgetRemainingTime, delegatedParkingRight.parkingRight.flexBudgetUnitType),
    };
  }

  return {
    ...data,
    status: <DelegeeStatus type={getDelegeeStatus(delegatedParkingRight.parkingRight)} />,
  };
};

const FleetMembersTable = (props: FleetMembersTableProps) => {
  const { onRevokeClicked, parkingProducts, getDelegatedParkingRights } = props;
  const { t } = useTranslation(["myProducts", "globals", "dataTable", "fleetManagerTable"]);

  const dispatch = useAppDispatch();
  const { myProductsState, setSorting } = useMyProductsContext();
  const { fleetManagerState } = useFleetManagerContext();
  const dataTableContext = useDataTableContext();
  const { appState } = useAppContext();

  const { delegatedParkingProducts } = parkingProducts;

  const useDirectVehicleAssignment = useAppSelector(selectUseDirectVehicleAssignment);
  const hasFlexBudgedSeasonTickets = parkingProducts.locatedParkingProducts.data?.some(p => p.hasFlexBudgedSeasonTickets);
  const getTotalOccupiedParkingRights = useGetOccupiedParkingRightsCount();


  const getFilters = (): DataTableContextFilters[] => [
    {
      key: FmpFilters.placeId,
      type: FilterType.QueryParam,
      value: myProductsState.selectedLocation.placeId.toString(),
    },
    {
      key: FmpFilters.sortBy,
      type: FilterType.QueryParam,
      value: myProductsState.dataTable.sortBy,
    },
    {
      key: FmpFilters.orderDesc,
      type: FilterType.QueryParam,
      value: myProductsState.dataTable.orderDesc?.toString(),
    },
    ...(dataTableContext.state.filters as DataTableContextFilters[])
  ];

  const getRequestModel = (): WithFiltersAndPaging<ForFleetManager> => {
    const request: WithFiltersAndPaging<ForFleetManager> = {
      entity: {
        seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId as string,
        language: appState.selectedLanguage,
      },
      pagination: {
        pageSize: dataTableContext.state.pageSize!,
        pageNumber: dataTableContext.state.pageNumber!,
      },
      filters: getFilters(),
    };

    return request;
  };

  const fetchDelegatedParkingRights = () => {
    if (appState.user.seasonTicketOwnerCrmId) {
      getDelegatedParkingRights(getRequestModel());
    }
  };

  const fetchLocatedParkingProducts = () => {
    if (appState.user.seasonTicketOwnerCrmId) {
      dispatch(parkingProductsThunks.getLocatedParkingProductsThunk(
        {seasonTicketOwnerCrmId: appState.user.seasonTicketOwnerCrmId, 
          language: appState.selectedLanguage }));
    }
  };

  useOnBulkImportFinishedCallback(() => {
    fetchDelegatedParkingRights();
    fetchLocatedParkingProducts();
});

  const memoizedFetch = useCallback(
    (): void => fetchDelegatedParkingRights(), // eslint-disable-next-line
    [
      dataTableContext.state.pageSize,
      dataTableContext.state.pageNumber,
      myProductsState.dataTable.orderDesc,
      myProductsState.dataTable.sortBy,
      myProductsState.selectedLocation.placeId,
      appState.user.seasonTicketOwnerCrmId,
      appState.selectedLanguage,
    ]
  );

  useEffect(() => {
    memoizedFetch();
  }, [memoizedFetch]);

  const productTableOptions = useProductOptions({ productType: ParkingProductType.ParkingRights });
  
  const selectedLocatedProduct = useCurrentlySelectedParkingProduct();
  const isGoBusinessProductSelected = selectedLocatedProduct?.contractType === ContractType.GoBusiness;
  const sharedProductActions = useSharedProductsMenuAction(isGoBusinessProductSelected);

  const columns: IFilteredColumn<IParkingRightsDataTableModel>[] = filteredColumnObjectBuilder.buildParkingRightsColumns(
    t, productTableOptions, hasFlexBudgedSeasonTickets
  );

  const onSort = (
    event: React.MouseEvent<unknown>,
    property: keyof IParkingRightsDataTableModel
  ) => {
    setSorting(property.toString());
  };

  const getTableRows = (): IParkingRightsDataTableModel[] => {
    if (
      !isNull(delegatedParkingProducts.pages) &&
      !isUndefined(delegatedParkingProducts.pages) &&
      !isUndefined(delegatedParkingProducts.pages[dataTableContext.state.pageNumber!])
    ) {
      return delegatedParkingProducts.pages[dataTableContext.state.pageNumber!].data?.map(dpr => _createData(dpr, hasFlexBudgedSeasonTickets)
      ) as IParkingRightsDataTableModel[];
    }

    return [];
  };

  const getLoadingState = () => {
    if (
      !isNull(delegatedParkingProducts.pages) &&
      !isUndefined(delegatedParkingProducts.pages) &&
      !isUndefined(delegatedParkingProducts.pages[dataTableContext.state.pageNumber!])
    ) {
      return delegatedParkingProducts.pages[dataTableContext.state.pageNumber!].loading;
    }

    return false;
  };

  const isLoading = getLoadingState();
  const rows = getTableRows();

  const getRegistrationIdByTableRow = (focusedRow: IDataTableRow | null): string | null => {
    const parkingRightId = focusedRow?.rowKey;
    const currentPageNumber = dataTableContext.state.pageNumber!;
    const currentPage = delegatedParkingProducts.pages?.[currentPageNumber];
    if (isNil(currentPage)) {
      return null;
    }

    const delegatedParkingRight = currentPage.data?.find(
      (delegatedPR) => delegatedPR.parkingRight.parkingRightId === parkingRightId
    );

    return delegatedParkingRight?.delegee.registrationId ?? null;
  };

  const isResendInviteDisabled = (focusedRow: IDataTableRow | null) => {
    const parkingRightRow = (focusedRow as IParkingRightsDataTableModel) || null;

    return parkingRightRow === null || parkingRightRow.status.props?.type !== Statuses.Pending;
  };

  return (
    <FleetManagerDataTable
      columns={columns}
      rows={rows}
      key={myProductsState.selectedLocation.placeId}
      isLoading={isLoading}
      orderBy={myProductsState.dataTable.sortBy ?? ""}
      totalPages={delegatedParkingProducts.totalPages}
      getRegistrationIdFromTableRow={getRegistrationIdByTableRow}
      isResendInviteDisabled={isResendInviteDisabled}
      resendAll={false}
      onFilterClicked={fetchDelegatedParkingRights}
      onResetFiltersClicked={fetchDelegatedParkingRights}
      onSort={onSort}
      translationOverrideKey={"parkingRights"}
      order={myProductsState.dataTable.orderDesc ? "desc" : "asc"}
      heading={{
        options: {
          useHeading: true,
          showHeadingCount: true,
        },
        title: useDirectVehicleAssignment ? t("globals:parkingRights") : t("delegatedParkingRights"),
        count: getTotalOccupiedParkingRights(selectedLocatedProduct || {})
      }}
      filtering={{
        options: {
          useFilters: true,
        },
      }}
      actions={{
        options: {
          useActions: true,
        },
        add: {
          useAdd: !sharedProductActions.menu.useMenu,
          showAdd: !sharedProductActions.menu.showMenu,
          addLabel: t("fleetManagerTable:addDelegee"),
          onAddClicked: sharedProductActions.onAddDelegee,
        },
        revoke: {
          useRevoke: true,
          showRevoke: fleetManagerState.selectedRowKeys.length > 0,
          revokeLabel: `${t("globals:revoke")} (${fleetManagerState.selectedRowKeys.length})`,
          onRevokeClicked: onRevokeClicked,
        },
        menu: sharedProductActions.menu,
      }}
      subHeading={props.subHeading}
    />
  );
};

const mapStateToProps = (state: RootReducer) => {
  const { parkingProducts } = state;

  return {
    parkingProducts,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  getDelegatedParkingRights: (request: WithFiltersAndPaging<ForFleetManager>) =>
    dispatch(parkingProductsActions.getDelegatedParkingProducts(request)),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(FleetMembersTable);
